import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { debounce } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FormCreateReportData } from 'dashboard/forms/FormCreateReport';
import { FormCsvUploadCreateReportData } from 'dashboard/forms/FormCsvUploadCreateReport';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ReportFilterCondition } from 'dashboard/lib/reports/filterMetas';
import { ExporterFileType } from 'dashboard/models/exporter';
import { ImporterReportAddressLookup } from 'dashboard/models/importer/report/address-lookup';
import { ImporterReportAdverseMedia } from 'dashboard/models/importer/report/adverse-media';
import { ImporterReportBusinessAdverseMedia } from 'dashboard/models/importer/report/business-adverse-media';
import { ImporterReportBusinessLookup } from 'dashboard/models/importer/report/business-lookup';
import { ImporterReportBusinessWatchlist } from 'dashboard/models/importer/report/business-watchlist';
import { ImporterReportEmailAddress } from 'dashboard/models/importer/report/email-address';
import { ImporterReportPhoneNumber } from 'dashboard/models/importer/report/phone-number';
import { ImporterReportPoliticallyExposedPerson } from 'dashboard/models/importer/report/politically-exposed-person';
import { ImporterReportProfileNonAuthoritative } from 'dashboard/models/importer/report/profile-non-authoritative';
import { ImporterReportWatchlist } from 'dashboard/models/importer/report/watchlist';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Report, ReportMatchResult, ReportStatus } from 'dashboard/models/report';
import {
  ReportTemplate,
  ReportTemplateFilterOption,
  SUPPORTED_BULK_UPLOAD_REPORT_TEMPLATE_TYPES,
} from 'dashboard/models/report-template';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  ReportsFilterParams,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/reports';
import { initialReportsFilterState } from 'dashboard/redux/reducers/reports';
import { RootState } from 'dashboard/redux/store';
import { loadReportTemplatesFilterOptions } from 'dashboard/redux/thunk-actions/report-templates';
import { loadExporterReport, loadReportsList } from 'dashboard/redux/thunk-actions/reports';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardReportsView from 'dashboard/views/DashboardReportsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { updateDateFilters } from 'lib/utils/filters';
import { assertNever } from 'lib/utils/types';

const DashboardReportsRoute = (props: PropsFromRedux) => {
  const hasCreatePermission = usePermission(Permission.ReportCreate);

  const createProductFeatureDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.ReportsCreate,
    props.currentEnvironment.type,
  );

  const allReportProductFeaturesDisabled =
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsAddressLookupCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsAdverseMediaCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsBusinessWatchlistCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsBusinessAdverseMediaCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsCryptoAddressWatchlistCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsEmailAddressCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsPhoneNumberCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsPoliticallyExposedPersonCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsScreeningCreate,
      props.currentEnvironment.type,
    ) &&
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsWatchlistCreate,
      props.currentEnvironment.type,
    );

  const exportersProductFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.Exporters,
    props.currentEnvironment.type,
  );

  const isCreateReportButtonDisabled =
    !hasCreatePermission || (createProductFeatureDisabled && allReportProductFeaturesDisabled);

  const navigate = useNavigate();
  const [selectedReportTemplateFilterOption, setSelectedReportTemplateFilterOption] =
    useState<ReportTemplateFilterOption>();

  // Required to abort requests while props are changing quickly
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadReportsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
  );

  const { loading: loadingReports, callback: loadReports } = useApiLoader({
    loader: () =>
      props.loadReportsList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        abortSignal: abortController.signal,
      }),
    deps: [
      props.loadReportsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load reports.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    abortController: abortController,
  });

  const { loading: loadReportTemplatesFilterOptions } = useApiLoader({
    loader: async () => await props.loadReportTemplatesFilterOptions({}),
    deps: [props.loadReportTemplatesFilterOptions],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Report),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: handleCsvUploadCreateReportsError, callback: handleCsvUploadCreateReports } =
    useApiCallback(
      async (
        data: FormCsvUploadCreateReportData,
        reportTemplateFilterOption: ReportTemplateFilterOption,
      ) => {
        if (
          !SUPPORTED_BULK_UPLOAD_REPORT_TEMPLATE_TYPES.includes(reportTemplateFilterOption.type)
        ) {
          props.showMessage(`Report type is not supported`, MessageType.Failure);
        } else {
          await createReportsForImport(reportTemplateFilterOption.type, {
            'report-template-id': reportTemplateFilterOption.id,
            'environment-id': props.currentEnvironment.id,
            file: data.file,
          });
          props.showMessage(`Successfully imported file`, MessageType.Success);
        }
      },
      [loadReports],
    );

  useApiErrorHandler({
    error: handleCsvUploadCreateReportsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create reports', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // Initially reload the reports filters state createdAtStart and createdAtEnd
  // This pattern will need to respected that we can always load the new, tz aware filters
  useEffect(() => {
    const initialFilters: ReportsFilterParams = initialReportsFilterState();
    props.setFilters({
      createdAtStart: initialFilters.createdAtStart,
      createdAtEnd: initialFilters.createdAtEnd,
    });
  }, [props.setFilters]);

  const { error: handleExportDataError, callback: handleExportData } = useApiCallback(
    async (fileType: ExporterFileType) => {
      await props.loadExporterReport({
        currentEnvironment: props.currentEnvironment,
        filters: props.filters || {},
        sortParams: { sort: '-', shouldSortDescending: false },
        fileType: fileType,
      });
      props.showMessage(
        'You will receive an email when your export is ready. See the Exports page for more.',
        MessageType.Notice,
      );
    },
    [props.currentEnvironment, props.sortParams, props.filters],
  );

  useApiErrorHandler({
    error: handleExportDataError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to create export, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const {
    handlePageLinkClick,
    handlePageSizeClick,
    handleDateUpdate,
    handleOmniSearch,
    handleSortUpdate,
  } = useTable({
    filters: props.filters || undefined,
    sortParams: props.sortParams || undefined,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) =>
      updateDateFilters(newDateRange, handleDateUpdate),
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = (status: ReportStatus[], matchResult: ReportMatchResult[]) => {
    props.setFilters({ status, matchResult });
    handlePageLinkClick(1);
  };

  const handleReportTemplateIdFilterUpdate = (reportTemplateIds: string[]) => {
    props.setFilters({ reportTemplateIds });
    handlePageLinkClick(1);
  };

  const handleTagIdFilterUpdate = (tagIds: string[]) => {
    props.setFilters({ tagIds });
    handlePageLinkClick(1);
  };

  const updateAdvancedFilter = useCallback(
    (conditions: ReportFilterCondition[][]) => {
      props.setFilters({ conditions });
      handlePageLinkClick(1);
    },
    [props.setFilters, handlePageLinkClick],
  );

  const handleAdvancedFilterUpdate = useMemo(
    () => debounce(500, updateAdvancedFilter),
    [updateAdvancedFilter],
  );

  const hasReportViewPermission = usePermission(Permission.ReportView);

  const { error: handleCreateReportError, callback: handleCreateReport } = useApiCallback(
    async (data: FormCreateReportData, reportTemplateFilterOption: ReportTemplateFilterOption) => {
      let payload;

      switch (reportTemplateFilterOption.type) {
        case ApiModel.ReportTemplateAddressLookup: {
          payload = {
            type: ApiModel.ReportAddressLookup as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-1': data.address.street1,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-2': data.address.street2,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-city': data.address.city,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-subdivision': data.address.subdivision,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-postal-code': data.address.postalCode,
            },
          };
          break;
        }
        case ApiModel.ReportTemplateAdverseMedia: {
          payload = {
            type: ApiModel.ReportAdverseMedia as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-middle': data['name-middle'],
              'name-last': data['name-last'],
              birthdate: data['birthdate'],
              'country-code': data['country-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessAdverseMedia: {
          payload = {
            type: ApiModel.ReportBusinessAdverseMedia as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              term: data['term'],
              'country-code': data['country-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessClassification: {
          payload = {
            type: ApiModel.ReportBusinessClassification as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              query: {
                'business-name': data['business-name'],
                website: data['website'],
                'address-street-1': data?.['address-street-1'],
                'address-street-2': data?.['address-street-2'],
                'address-city': data?.['address-city'],
                'address-subdivision': data?.['address-subdivision'],
                'address-postal-code': data?.['address-postal-code'],
                'address-country-code': data?.['address-country-code'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessLookup: {
          const associatedPeople = [];
          if (data['associated-person-1-name-first']) {
            associatedPeople.push({
              'name-first': data['associated-person-1-name-first'],
              'name-last': data['associated-person-1-name-last'],
            });
          }
          if (data['associated-person-2-name-first']) {
            associatedPeople.push({
              'name-first': data['associated-person-2-name-first'],
              'name-last': data['associated-person-2-name-last'],
            });
          }
          if (data['associated-person-3-name-first']) {
            associatedPeople.push({
              'name-first': data['associated-person-3-name-first'],
              'name-last': data['associated-person-3-name-last'],
            });
          }
          payload = {
            type: ApiModel.ReportBusinessLookup as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              query: {
                'business-name': data['business-name'],
                'phone-number': data['phone-number'],
                website: data['website'],
                'associated-people': associatedPeople,
                'address-street-1': data.address?.street1,
                'address-street-2': data.address?.street2,
                'address-city': data.address?.city,
                'address-subdivision': data.address?.subdivision,
                'address-postal-code': data.address?.postalCode,
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessNonprofit: {
          payload = {
            type: ApiModel.ReportBusinessNonprofit as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              query: {
                ein: data['ein'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessOnlinePresence: {
          payload = {
            type: ApiModel.ReportBusinessOnlinePresence as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              query: {
                'business-name': data['business-name'],
                'address-street-1': data?.['address-street-1'],
                'address-street-2': data?.['address-street-2'],
                'address-city': data?.['address-city'],
                'address-subdivision': data?.['address-subdivision'],
                'address-postal-code': data?.['address-postal-code'],
                'address-country-code': data['address-country-code'],
                website: data?.['website'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessSocialMedia: {
          payload = {
            type: ApiModel.ReportBusinessSocialMedia as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              name: data['name'],
              ticker: data['ticker'],
              website: data['website'],
              profile: data['profile'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessWatchlist: {
          payload = {
            type: ApiModel.ReportBusinessWatchlist as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              term: data['term'],
              'country-code': data['country-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateCryptoAddressWatchlist: {
          payload = {
            type: ApiModel.ReportCryptoAddressWatchlist as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              query: {
                'crypto-address': data['crypto-address'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateCustomList: {
          // Create is not supported for this report type yet
          payload = {
            type: ApiModel.ReportCustomList as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
            },
          };
          break;
        }
        case ApiModel.ReportTemplateEmailAddress: {
          payload = {
            type: ApiModel.ReportEmailAddress as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'email-address': data['email-address'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateFinraBrokerCheck: {
          payload = {
            type: ApiModel.ReportFinraBrokerCheck as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'broker-type': data['broker-type'],
              'crd-number': data['crd-number'],
              'name-full': data['name-full'],
              'firm-name': data['firm-name'],
              'address-city': data['address-city'],
              'address-subdivision': data['address-subdivision'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplatePhoneNumber: {
          payload = {
            type: ApiModel.ReportPhoneNumber as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'phone-number': data['phone-number'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplatePoliticallyExposedPerson: {
          payload = {
            type: ApiModel.ReportPoliticallyExposedPerson as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              query: {
                'name-first': data['name-first'],
                'name-middle': data['name-middle'],
                'name-last': data['name-last'],
                birthdate: data['birthdate'],
                'country-code': data['country-code'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateProfile:
          payload = {
            type: ApiModel.ReportProfile as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-last': data['name-last'],
              birthdate: data['birthdate'],
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore: TODO figure this typing out
              'social-security-number': data['social-security-number'],
              // profile reports only work in the US
              'country-code': 'US',
              'address-street-1': data.address?.street1,
              'address-street-2': data.address?.street2,
              'address-city': data.address?.city,
              'address-subdivision': data.address?.subdivision,
              'address-postal-code': data.address?.postalCode,
              'phone-number': data['phone-number'],
            },
          };
          break;
        case ApiModel.ReportTemplateProfileNonAuthoritative: {
          payload = {
            type: ApiModel.ReportProfileNonAuthoritative as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              query: {
                'phone-number': data['phone-number'],
                'email-address': data['email-address'],
                'name-first': data['name-first'],
                'name-middle': data['name-middle'],
                'name-last': data['name-last'],
                'address-city': data['address-city'],
                'address-subdivision': data['address-subdivision'],
                'address-postal-code': data['address-postal-code'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateScreening: {
          payload = {
            type: ApiModel.ReportScreening as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              query: {
                term: data['term'],
                birthdate: data['birthdate'],
                'address-street-1': data['address-street-1'],
                'address-street-2': data['address-street-2'],
                'address-city': data['address-city'],
                'address-subdivision': data['address-subdivision'],
                'address-postal-code': data['address-postal-code'],
                'address-country-code': data['address-country-code'],
              },
            },
          };
          break;
        }
        case ApiModel.ReportTemplateSecActionLookup: {
          payload = {
            type: ApiModel.ReportSecActionLookup as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-last': data['name-last'],
              birthdate: data['birthdate'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateSentilinkScores: {
          payload = {
            type: ApiModel.ReportSentilinkScores as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              birthdate: data['birthdate'],
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-middle': data['name-middle'],
              'name-last': data['name-last'],
              'social-security-number': data['social-security-number'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-1': data.address.street1,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-2': data.address.street2,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-city': data.address.city,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-subdivision': data.address.subdivision,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-postal-code': data.address.postalCode,
              'country-code': data['country-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateSocialMedia: {
          payload = {
            type: ApiModel.ReportSocialMedia as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'phone-number': data['phone-number'],
              'email-address': data['email-address'],
              'name-first': data['name-first'],
              'name-middle': data['name-middle'],
              'name-last': data['name-last'],
              'address-city': data['address-city'],
              'address-subdivision': data['address-subdivision'],
              'address-postal-code': data['address-postal-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateSynthetic: {
          payload = {
            type: ApiModel.ReportSynthetic as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              birthdate: data['birthdate'],
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-middle': data['name-middle'],
              'name-last': data['name-last'],
              'social-security-number': data['social-security-number'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-1': data.address.street1,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-street-2': data.address.street2,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-city': data.address.city,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-subdivision': data.address.subdivision,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'address-postal-code': data.address.postalCode,
              'country-code': data['country-code'],
            },
          };
          break;
        }
        case ApiModel.ReportTemplateWatchlist: {
          payload = {
            type: ApiModel.ReportWatchlist as const,
            attributes: {
              'environment-id': props.currentEnvironment.id,
              'report-template-id': reportTemplateFilterOption.id,
              'reference-id': data['reference-id'],
              'name-first': data['name-first'],
              'name-middle': data['name-middle'],
              'name-last': data['name-last'],
              birthdate: data['birthdate'],
              'country-code': data['country-code'],
            },
          };
          break;
        }
        // We don't support creating the following report types
        // through dashboard
        case ApiModel.ReportTemplateBusinessAssociatedPersons: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessIndustryClassification: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessLiensLookup: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessRegistrationsLookup: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateBusinessWebsite: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateChainalysisAddressScreening: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateClearbitBusinessLookup:
          {
            payload = {
              type: null,
              attributes: {},
            };
          }
          break;
        case ApiModel.ReportTemplateCoinbaseCheckCryptoRisk:
          {
            payload = {
              type: null,
              attributes: {},
            };
          }
          break;
        case ApiModel.ReportTemplateEquifaxOneview: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateKyckrBusinessLookup: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateMiddeskBusinessLookup: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateMxAccount: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateSentilinkApplicationRisk: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        case ApiModel.ReportTemplateTrmWalletScreening: {
          payload = {
            type: null,
            attributes: {},
          };
          break;
        }
        default:
          assertNever(reportTemplateFilterOption.type);
      }

      const response = await SessionClientDashboard.postResourceCreate<Report>(payload, {
        contentType: 'multipart/form-data',
      });

      if (hasReportViewPermission) {
        navigate(`/dashboard/reports/${response.data.id}`);
      } else {
        await loadReports();
      }

      props.showMessage('Successfully created report', MessageType.Success);
    },
    [loadReports, hasReportViewPermission],
  );

  useApiErrorHandler({
    error: handleCreateReportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create report', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleReportTemplateFilterOptionSelect = (reportTemplateId?: string) => {
    setSelectedReportTemplateFilterOption(
      props.reportTemplateFilterOptions.find((rt) => rt.id === reportTemplateId),
    );
  };

  return (
    <DashboardReportsView
      currentEnvironment={props.currentEnvironment}
      exportersProductFeatureIsDisabled={exportersProductFeatureIsDisabled}
      isCreateReportButtonDisabled={isCreateReportButtonDisabled}
      reports={props.reports}
      reportTemplates={props.reportTemplates}
      reportTemplateFilterOptions={props.reportTemplateFilterOptions}
      tags={props.tags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedReportTemplateFilterOption={selectedReportTemplateFilterOption}
      loadingReports={loadingReports}
      loadingReportTemplates={loadReportTemplatesFilterOptions}
      loadingTags={loadingTags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
      handleCreateReport={handleCreateReport}
      handleCsvUploadCreateReports={handleCsvUploadCreateReports}
      handleReportTemplateFilterOptionSelect={handleReportTemplateFilterOptionSelect}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleReportTemplateIdFilterUpdate={handleReportTemplateIdFilterUpdate}
      handleTagIdFilterUpdate={handleTagIdFilterUpdate}
      handleAdvancedFilterUpdate={handleAdvancedFilterUpdate}
      handleExportData={handleExportData}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    featureFlags: state.featureFlags.featureFlags,
    organization: state.organization.organization,
    reports: state.reports.reports[state.currentEnvironment.currentEnvironment.id] || [],
    reportTemplates: state.reportTemplates.reportTemplates,
    reportTemplateFilterOptions: state.reportTemplates.filterOptions,
    tags: state.tags.tags.report,
    paginationParams: state.reports.paginationParams,
    sortParams: state.reports.sortParams,
    filters: state.reports.filters,
    currentPage: state.reports.currentPage,
    pageSize: state.reports.pageSize,
  };
};

const mapDispatchToProps = {
  loadExporterReport,
  loadReportsList,
  loadReportTemplatesFilterOptions,
  loadTags,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
};

const createReportsForImport = (
  reportTemplateType: ReportTemplate['type'],
  attributes: {
    'report-template-id': string;
    'environment-id': string;
    file: File;
  },
): Promise<unknown> => {
  switch (reportTemplateType) {
    case ApiModel.ReportTemplateAddressLookup: {
      return SessionClientDashboard.postResourceCreate<ImporterReportAddressLookup>(
        {
          attributes,
          type: ApiModel.ImporterReportAddressLookup,
        },
        { contentType: 'multipart/form-data' },
      );
    }
    case ApiModel.ReportTemplateAdverseMedia: {
      return SessionClientDashboard.postResourceCreate<ImporterReportAdverseMedia>(
        {
          attributes,
          type: ApiModel.ImporterReportAdverseMedia,
        },
        { contentType: 'multipart/form-data' },
      );
    }
    case ApiModel.ReportTemplateBusinessAdverseMedia: {
      return SessionClientDashboard.postResourceCreate<ImporterReportBusinessAdverseMedia>(
        {
          attributes,
          type: ApiModel.ImporterReportBusinessAdverseMedia,
        },
        { contentType: 'multipart/form-data' },
      );
    }
    case ApiModel.ReportTemplateBusinessLookup: {
      return SessionClientDashboard.postResourceCreate<ImporterReportBusinessLookup>(
        {
          attributes,
          type: ApiModel.ImporterReportBusinessLookup,
        },
        { contentType: 'multipart/form-data' },
      );
    }
    case ApiModel.ReportTemplateBusinessWatchlist: {
      return SessionClientDashboard.postResourceCreate<ImporterReportBusinessWatchlist>(
        {
          attributes,
          type: ApiModel.ImporterReportBusinessWatchlist,
        },
        { contentType: 'multipart/form-data' },
      );
    }
    case ApiModel.ReportTemplateEmailAddress: {
      return SessionClientDashboard.postResourceCreate<ImporterReportEmailAddress>(
        {
          attributes,
          type: ApiModel.ImporterReportEmailAddress,
        },
        { contentType: 'multipart/form-data' },
      );
    }

    case ApiModel.ReportTemplatePhoneNumber: {
      return SessionClientDashboard.postResourceCreate<ImporterReportPhoneNumber>(
        {
          attributes,
          type: ApiModel.ImporterReportPhoneNumber,
        },
        { contentType: 'multipart/form-data' },
      );
    }

    case ApiModel.ReportTemplatePoliticallyExposedPerson: {
      return SessionClientDashboard.postResourceCreate<ImporterReportPoliticallyExposedPerson>(
        {
          attributes,
          type: ApiModel.ImporterReportPoliticallyExposedPerson,
        },
        { contentType: 'multipart/form-data' },
      );
    }

    case ApiModel.ReportTemplateProfileNonAuthoritative: {
      return SessionClientDashboard.postResourceCreate<ImporterReportProfileNonAuthoritative>(
        {
          attributes,
          type: ApiModel.ImporterReportProfileNonAuthoritative,
        },
        { contentType: 'multipart/form-data' },
      );
    }

    // TODO(maggie): add import for screening report
    case ApiModel.ReportTemplateWatchlist: {
      return SessionClientDashboard.postResourceCreate<ImporterReportWatchlist>(
        {
          attributes,
          type: ApiModel.ImporterReportWatchlist,
        },
        { contentType: 'multipart/form-data' },
      );
    }

    /* Everything falls through to default */
    case ApiModel.ReportTemplateBusinessIndustryClassification:
    case ApiModel.ReportTemplateBusinessSocialMedia:
    case ApiModel.ReportTemplateCryptoAddressWatchlist:
    case ApiModel.ReportTemplateFinraBrokerCheck:
    case ApiModel.ReportTemplateProfile:
    case ApiModel.ReportTemplateSecActionLookup:
    case ApiModel.ReportTemplateSynthetic:
    default: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return null;
    }
  }
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportsRoute);
